<template>
  <el-card
    shadow="never"
    class="monitoring-block monitoring-family-block"
    :class="urgencyClass"
  >
    <div
      slot="header"
      class="monitoring-block__header"
    >
      <span class="title">{{ $t(definition.titleKey) }}</span>
      <MonitoringBlockHint />
    </div>

    <slot name="note" />

    <el-table
      :data="displayRows"
      size="small"
      border
      :span-method="spanMethod"
      :empty-text="$t('monitoringDashboard.empty')"
      max-height="420"
    >
      <!-- First column, so it carries the full-width skeleton. -->
      <el-table-column
        prop="laboratory"
        :label="$t('monitoringDashboard.family.laboratory')"
        width="100"
      >
        <template slot-scope="{ row }">
          <MonitoringSkeleton v-if="row.isSkeleton" />
          <span v-else>{{ row.laboratory }}</span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('monitoringDashboard.family.familycode')">
        <template slot-scope="{ row }">
          <span>
            {{
              row.familyDescription ||
              $t('monitoringDashboard.family.unknownFamily')
            }}
          </span>
        </template>
      </el-table-column>

      <el-table-column
        v-for="col in columns"
        :key="col.key"
        :label="$t(col.labelKey)"
        align="center"
      >
        <template slot-scope="{ row }">
          <button
            type="button"
            class="monitoring-count"
            :class="{ 'monitoring-count--zero': bucketValue(row, col) === 0 }"
            :disabled="bucketValue(row, col) === 0"
            @click="debouncedClick(row, col)"
          >
            {{ bucketValue(row, col) }}
          </button>
        </template>
      </el-table-column>
    </el-table>
  </el-card>
</template>

<script>
  import debounce from 'lodash.debounce';
  import { AWAITING_PROCESSING_COLUMNS } from '@/config/monitoringDashboard.config';
  import MonitoringBlockHint from './MonitoringBlockHint.vue';
  import MonitoringSkeleton from './MonitoringSkeleton.vue';

  const SKELETON_ROWS = 5;
  const SKELETON_COLSPAN = 99;

  export default {
    name: 'MonitoringFamilyBlock',
    components: { MonitoringBlockHint, MonitoringSkeleton },
    props: {
      definition: {
        type: Object,
        required: true,
      },
      data: {
        type: Array,
        default: () => [],
      },
      urgency: {
        type: String,
        default: 'safe',
        validator: (v) => ['critical', 'warning', 'safe'].includes(v),
      },
      loading: {
        type: Boolean,
        default: false,
      },
    },
    computed: {
      urgencyClass() {
        return `urgency-${this.urgency}`;
      },
      rows() {
        return this.data || [];
      },
      displayRows() {
        if (!this.loading) return this.rows;
        return Array.from({ length: SKELETON_ROWS }, () => ({
          isSkeleton: true,
        }));
      },
    },
    data() {
      return {
        columns: AWAITING_PROCESSING_COLUMNS,
        debouncedClick: null,
      };
    },
    created() {
      this.debouncedClick = debounce(this.onClick, 300);
    },
    beforeDestroy() {
      this.debouncedClick.cancel();
    },
    methods: {
      spanMethod({ row, columnIndex }) {
        if (!row.isSkeleton) return [1, 1];
        return columnIndex === 0 ? [1, SKELETON_COLSPAN] : [0, 0];
      },
      bucketValue(row, col) {
        return row[col.dataKey] || 0;
      },
      onClick(row, col) {
        const count = this.bucketValue(row, col);
        if (!count) return;
        const family =
          row.familyDescription ||
          this.$t('monitoringDashboard.family.unknownFamily');
        this.$emit('drill', {
          blockKey: this.definition.blockKey,
          statusKey: null,
          // bucketKey carries the BE enum (d1/d2to3/…) — col.dataKey, not col.key.
          bucketKey: col.dataKey,
          laboratory: row.laboratory,
          familyCode: row.familyCode,
          count,
          label: `${row.laboratory} ${family} – ${this.$t(col.labelKey)}`,
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .monitoring-family-block {
    background: #2e3454;
    border-style: solid;
    border-color: rgba(#fff, 0.06);
    border-width: 1px;
    border-left-width: 4px;
    border-radius: 4px 12px 12px 4px;
    box-shadow: 0 10px 25px -5px rgba(#000, 0.3),
      0 4px 12px -2px rgba(#000, 0.2);
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      transform: translateY(-3px);
      background: #333a5e;
      border-color: rgba(#fff, 0.12);
      box-shadow: 0 20px 30px -8px rgba(#000, 0.45);
    }

    ::v-deep .el-card__body {
      padding: 20px 24px;
    }

    &.urgency-critical {
      border-left-color: #ef4444;
    }

    &.urgency-warning {
      border-left-color: #f59e0b;
    }

    &.urgency-safe {
      border-left-color: #10b981;
    }
  }

  .monitoring-count {
    background: none;
    border: none;
    cursor: pointer;
    font-weight: 700;
    color: #6366f1;
    text-decoration: underline;
    padding: 0;

    &:hover {
      color: #818cf8;
    }

    &:disabled,
    &--zero {
      color: #64748b;
      cursor: default;
      text-decoration: none;

      &:hover {
        color: #64748b;
      }
    }
  }

  .monitoring-block__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .title {
    color: #ffffff;
    font-weight: 600;
  }

  ::v-deep .el-table {
    background-color: #1e2235;
    border: 1px solid #323854;
    border-collapse: collapse;
    color: #cbd5e1;

    &::before,
    &::after {
      background-color: #323854;
    }

    thead tr th {
      background-color: #2a2f4a;
      color: #ffffff;
      font-weight: 600;
      padding: 8px 0;
      border-bottom: 1px solid #323854;
    }

    tbody tr td {
      background-color: #1e2235;
      padding: 4px 0;
      border-bottom: none;
    }

    tbody tr:hover td {
      background-color: #2e3557;
      color: #ffffff;
    }

    &.el-table--border th,
    &.el-table--border td {
      border-right: 1px solid #323854;
    }

    .el-table__empty-block {
      background-color: #1e2235;
    }

    .el-table__empty-text {
      color: #64748b;
    }
  }

  .cell-overdue {
    color: #ef4444 !important;
    font-weight: 600;
  }

  .cell-imminent {
    color: #f59e0b !important;
    font-weight: 600;
  }

  .cell-current {
    color: #10b981 !important;
  }
</style>
